import type { ClientGameContext } from '@pixelworld/game-core/client';
import type {
  ChatAccess,
  ChatAccessRequest,
  ChatDecisionResponse,
  ChatMessage,
  ChatRequestOutcome,
  ChatRequestResponse,
  ChatSendResponse,
  FriendPush,
  GameId,
  JoinRoomErrorCode,
  JoinRoomResponse,
  PlayerReportResponse,
  RoomCode,
  RoomPlayerInfo,
  RoomVisibility,
  ServerToClientEvents,
} from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { createGameSocket, type GameSocket } from '../../lib/socket';
import { mutedPlayers } from '../chat/muted-players';
import { restoreUrl, serverIsUp, waitForServer } from './server-update';
import type { WalletUpdates } from '../wallet/CoinCounter';

export type RoomConnection =
  | { status: 'connecting' }
  | { status: 'failed'; error: JoinRoomErrorCode }
  | { status: 'disconnected' }
  /** The same account started playing in another tab or on another device. */
  | { status: 'replaced' }
  /** The session has expired or was logged out. */
  | { status: 'unauthorized' }
  | {
      status: 'joined';
      code: RoomCode;
      players: RoomPlayerInfo[];
      context: ClientGameContext;
      visibility: RoomVisibility;
      /** Who may write freely in a public room (ADR 0011). */
      chatAccess: ChatAccess;
      chat: RoomChat;
      friends: RoomFriends;
      wallet: WalletUpdates;
      /** The connection dropped and is being restored. The game stays on screen meanwhile. */
      reconnecting: boolean;
      /** The server is being updated. The page loads again by itself when it is back. */
      updating: boolean;
    };

/** Free text (ADR 0008), and in public rooms asking the host for it (ADR 0011). */
export interface RoomChat {
  send(text: string): Promise<ChatSendResponse>;
  /** Every message, also from muted players: the chat box decides what to show. */
  onMessage(listener: (message: ChatMessage) => void): () => void;
  /** Ask the host to write freely. */
  request(): Promise<ChatRequestResponse>;
  /** Host only: say yes or no to a request, or take a permission back. */
  decide(playerId: string, allow: boolean): Promise<ChatDecisionResponse>;
  /** Host only: the requests that wait for an answer. Called right away with the latest list. */
  onRequests(listener: (requests: ChatAccessRequest[]) => void): () => void;
  /** The host's answer to this player's request. */
  onAnswer(listener: (outcome: ChatRequestOutcome) => void): () => void;
  /** Tell an adult about another player in the room (ADR 0018). */
  report(playerId: string): Promise<PlayerReportResponse>;
}

/** Friend requests, invitations and knocks while in the room (ADR 0019). */
export interface RoomFriends {
  onPush(listener: (push: FriendPush) => void): () => void;
  /** Let a friend who knocks into this room, or not. */
  answerKnock(playerId: string, allow: boolean): void;
}

/** How to get into a room when there is no code: a new public or private room, or "play now". */
export type RoomEntry = 'public' | 'private' | 'quick';

interface RoomRequest {
  gameId: GameId;
  /** Join this room. When `null`, `friendId` or `entry` decides. */
  code: RoomCode | null;
  /** Go to this friend's room (ADR 0019): knock on a room with a code, or accept an invitation. */
  friendId: string | null;
  entry: RoomEntry;
  /** Coming back after a server update: open `code` again with this visibility if it is gone. */
  restore: RoomVisibility | null;
}

/** How long to wait for the server to come back after an update before giving up. */
const UPDATE_TIMEOUT_MS = 3 * 60_000;
const UPDATE_POLL_MS = 2000;

const exposeDebugHooks = import.meta.env.VITE_E2E === 'true';

/**
 * Connects to the server, creates or joins a room and keeps the player list
 * up to date. A dropped connection is restored into the same room. Disconnects
 * when the component unmounts.
 */
export function useRoomConnection({
  gameId,
  code,
  friendId,
  entry,
  restore,
}: RoomRequest): RoomConnection {
  const [connection, setConnection] = useState<RoomConnection>({ status: 'connecting' });

  useEffect(() => {
    const socket = createGameSocket();
    let joinedCode: RoomCode | null = null;
    let joinedVisibility: RoomVisibility = 'public';
    let finished = false;
    let unmounted = false;
    let serverRestarting = false;
    const finish = (state: RoomConnection) => {
      finished = true;
      setConnection(state);
      socket.disconnect();
    };
    /**
     * The room lived in the old server's memory. Wait for the new server, then load the page
     * again: that brings the new game code and opens the room again under the same code.
     */
    const comeBackAfterUpdate = (room: RoomCode) => {
      finished = true;
      socket.disconnect();
      setConnection((current) =>
        current.status === 'joined' ? { ...current, reconnecting: false, updating: true } : current,
      );
      void waitForServer({
        isUp: serverIsUp,
        intervalMs: UPDATE_POLL_MS,
        timeoutMs: UPDATE_TIMEOUT_MS,
      }).then((up) => {
        if (unmounted) return;
        if (up) window.location.assign(restoreUrl(gameId, room, joinedVisibility));
        else setConnection({ status: 'disconnected' });
      });
    };

    socket.on('room:playerJoined', (player) => {
      setConnection((current) =>
        current.status === 'joined'
          ? { ...current, players: [...current.players, player] }
          : current,
      );
    });
    socket.on('room:playerLeft', ({ playerId }) => {
      setConnection((current) =>
        current.status === 'joined'
          ? { ...current, players: current.players.filter((player) => player.id !== playerId) }
          : current,
      );
    });
    socket.on('chat:access', (chatAccess) => {
      setConnection((current) =>
        current.status === 'joined' ? { ...current, chatAccess } : current,
      );
    });
    socket.on('session:replaced', () => finish({ status: 'replaced' }));
    socket.on('server:restarting', () => {
      serverRestarting = true;
      setConnection((current) =>
        current.status === 'joined' ? { ...current, updating: true } : current,
      );
    });

    socket.on('disconnect', () => {
      if (finished) return;
      if (serverRestarting && joinedCode) {
        comeBackAfterUpdate(joinedCode);
        return;
      }
      if (!socket.active) {
        // The server closed the connection on purpose; Socket.IO will not retry.
        finish({ status: 'disconnected' });
        return;
      }
      setConnection((current) =>
        current.status === 'joined' ? { ...current, reconnecting: true } : current,
      );
    });
    socket.on('connect_error', (error) => {
      if (error.message === 'UNAUTHORIZED') {
        finish({ status: 'unauthorized' });
      } else if (!socket.active) {
        // The server refused the connection (not a network error), so there is no retry.
        finish({ status: 'disconnected' });
      }
    });
    socket.io.on('reconnect_failed', () => finish({ status: 'disconnected' }));

    // Every connection after the first one goes back into the same room.
    socket.on('connect', () => {
      if (!joinedCode || finished) return;
      const room = joinedCode;
      socket.emit('room:join', { code: room }, (response) => {
        if (!response.ok) {
          // The server was updated or restarted without warning.
          if (response.error === 'ROOM_NOT_FOUND') comeBackAfterUpdate(room);
          else finish({ status: 'failed', error: response.error });
          return;
        }
        setConnection((current) =>
          current.status === 'joined'
            ? {
                ...current,
                players: response.players,
                chatAccess: response.chatAccess,
                reconnecting: false,
              }
            : current,
        );
      });
    });

    const handleFirstJoin = (response: JoinRoomResponse) => {
      if (!response.ok) {
        finish({ status: 'failed', error: response.error });
        return;
      }
      joinedCode = response.code;
      joinedVisibility = response.visibility;
      setConnection({
        status: 'joined',
        code: response.code,
        players: response.players,
        context: createGameContext(socket, response, exposeDebugHooks),
        visibility: response.visibility,
        chatAccess: response.chatAccess,
        chat: createRoomChat(socket),
        friends: createRoomFriends(socket),
        wallet: createWalletUpdates(socket),
        reconnecting: false,
        updating: false,
      });
    };

    // Deferred so React StrictMode's mount → unmount → mount in development
    // does not create two rooms.
    const start = setTimeout(() => {
      socket.connect();
      if (code) {
        socket.emit(
          'room:join',
          restore ? { code, restore: { gameId, visibility: restore } } : { code },
          handleFirstJoin,
        );
      } else if (friendId) {
        socket.emit('room:joinFriend', { gameId, friendId }, handleFirstJoin);
      } else if (entry === 'quick') {
        socket.emit('room:quickJoin', { gameId }, handleFirstJoin);
      } else {
        socket.emit('room:create', { gameId, visibility: entry }, handleFirstJoin);
      }
    }, 0);

    return () => {
      clearTimeout(start);
      finished = true;
      unmounted = true;
      socket.removeAllListeners();
      socket.io.removeAllListeners();
      // Leaving the page is leaving the room; only a lost connection keeps the player's place.
      // Closing right away can drop the message, so wait for the server's answer (at most 1 s).
      if (socket.connected && joinedCode) {
        socket.timeout(1000).emit('room:leave', () => socket.disconnect());
      } else {
        socket.disconnect();
      }
    };
  }, [gameId, code, friendId, entry, restore]);

  return connection;
}

function createGameContext(
  socket: GameSocket,
  joined: Extract<JoinRoomResponse, { ok: true }>,
  exposeDebugHooks: boolean,
): ClientGameContext {
  const subscribe = <E extends keyof ServerToClientEvents>(
    event: E,
    listener: ServerToClientEvents[E],
  ) => {
    // Socket.IO's typed `on`/`off` need the listener cast to its own generic signature.
    socket.on(event, listener as never);
    return () => {
      socket.off(event, listener as never);
    };
  };

  return {
    roomCode: joined.code,
    self: joined.self,
    players: joined.players,
    initialState: joined.state,
    exposeDebugHooks,
    // Input is only useful live. Sending it while offline would replay stale moves later.
    sendInput: (input) => {
      if (socket.connected) socket.emit('game:input', input);
    },
    onSnapshot: (listener) => subscribe('game:snapshot', listener),
    onEvent: (listener) => subscribe('game:event', listener),
    onPlayerJoined: (listener) => subscribe('room:playerJoined', listener),
    onPlayerLeft: (listener) => subscribe('room:playerLeft', ({ playerId }) => listener(playerId)),
    onChatMessage: (listener) =>
      subscribe('chat:message', (message) => {
        if (!mutedPlayers.has(message.playerId)) listener(message);
      }),
  };
}

function createRoomFriends(socket: GameSocket): RoomFriends {
  return {
    onPush: (listener) => {
      socket.on('friend:push', listener);
      return () => {
        socket.off('friend:push', listener);
      };
    },
    answerKnock: (playerId, allow) => {
      if (socket.connected) socket.emit('friend:knockAnswer', { playerId, allow });
    },
  };
}

function createWalletUpdates(socket: GameSocket): WalletUpdates {
  return {
    onChange: (listener) => {
      socket.on('wallet:changed', listener);
      return () => {
        socket.off('wallet:changed', listener);
      };
    },
  };
}

function createRoomChat(socket: GameSocket): RoomChat {
  /** Sends a chat event and waits for the answer; `null` when it does not come. */
  const ask = async <T>(send: () => Promise<unknown>): Promise<T | null> => {
    if (!socket.connected) return null;
    try {
      // Socket.IO types the answer of a call with a timeout loosely.
      return (await send()) as T;
    } catch {
      return null;
    }
  };
  const failed = { ok: false, error: 'INVALID_REQUEST' } as const;

  // Kept, so a host who opens the chat box later still sees who is waiting.
  let requests: ChatAccessRequest[] = [];
  const requestListeners = new Set<(requests: ChatAccessRequest[]) => void>();
  socket.on('chat:requests', (latest) => {
    requests = latest;
    for (const listener of requestListeners) listener(latest);
  });

  return {
    send: async (text) =>
      (await ask<ChatSendResponse>(() =>
        socket.timeout(5000).emitWithAck('chat:send', { text }),
      )) ?? failed,
    request: async () =>
      (await ask<ChatRequestResponse>(() => socket.timeout(5000).emitWithAck('chat:request'))) ??
      failed,
    report: async (playerId) =>
      (await ask<PlayerReportResponse>(() =>
        socket.timeout(5000).emitWithAck('player:report', { playerId }),
      )) ?? { ok: false, error: 'UNAVAILABLE' },
    decide: async (playerId, allow) =>
      (await ask<ChatDecisionResponse>(() =>
        socket.timeout(5000).emitWithAck('chat:decide', { playerId, allow }),
      )) ?? failed,
    onMessage: (listener) => {
      socket.on('chat:message', listener);
      return () => {
        socket.off('chat:message', listener);
      };
    },
    onRequests: (listener) => {
      requestListeners.add(listener);
      listener(requests);
      return () => {
        requestListeners.delete(listener);
      };
    },
    onAnswer: (listener) => {
      socket.on('chat:answer', listener);
      return () => {
        socket.off('chat:answer', listener);
      };
    },
  };
}
